import { cx } from '@emotion/css';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { Fragment, useState } from 'react';

import { DirectBriefingButton } from '_components/Briefing/versions/DirectBriefing/button';
import { ClampLines } from '_components/common/ClampLines';
import { agencyProfileConfig } from '_features/AgencyProfile/config';

import type { ServiceNode, ServiceSkillNode } from './types';

interface Props {
  agencySlug: string;
  description: string;
  serviceName: string;
  skills: ServiceSkillNode[];
  className?: string;
  clampLines?: number;
}

export const ServiceAbout = (props: Props) => {
  const { agencySlug, className, description, skills, serviceName, clampLines = 7 } = props;
  const { t } = useTranslation(agencyProfileConfig.i18nNamespaces);
  const [toggledSkills, setToggledSkills] = useState(false);
  const maxNumberOfSkillsDisplayed = 10;

  return (
    <Fragment>
      <section className={cx(className, 'p-4')}>
        <header className="p-2">
          <span className="bold">{t('agency:profile.services.descriptionTitle')}</span>
        </header>
        <div className="layout-row">
          <div className="p-2 flex-1">
            {description.length > 0 ? (
              <ClampLines text={description} lines={clampLines} />
            ) : (
              <p className="text-secondary-700 italic">{t('agency:profile.services.noDescription')}</p>
            )}
          </div>
          <div className="p-2 hide show-gt-sm flex-20 layout-column layout-align-start-stretch">
            <DirectBriefingButton
              cta="service-about"
              agencySlug={agencySlug}
              size={'sm'}
              truncate={false}
              buttonStyle="primary"
              buttonVariant="raised"
              label={t('agency:profile.services.quoteCtaBtn')}
            />
          </div>
        </div>
      </section>
      {skills.length > 0 && (
        <section className="p-4 layout-column">
          <header className="p-2">
            <span>
              <Trans i18nKey={'agency:profile.services.skillsTitle'} count={skills.length}>
                <b>Skills</b> in {{ serviceName }} ({{ count: skills.length }})
              </Trans>
            </span>
          </header>
          <div className="p-2 layout-row layout-wrap">
            {(skills.length > maxNumberOfSkillsDisplayed && toggledSkills
              ? skills
              : skills.slice(0, maxNumberOfSkillsDisplayed)
            ).map((skill) => (
              <span
                key={skill.slug}
                className="mr-2 mb-2 bg-secondary-300 rounded-xs px-2 py-1 small text-secondary-900">
                {skill.name}
              </span>
            ))}
            {skills.length > maxNumberOfSkillsDisplayed && (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <a
                tabIndex={0}
                role={'button'}
                className="cursor-pointer mb-2 bg-secondary-300 rounded-xs px-2 py-1 small text-secondary-900"
                onClick={() => setToggledSkills(!toggledSkills)}
                onKeyDown={() => setToggledSkills(!toggledSkills)}>
                {toggledSkills ? t('common:clampLines.seeLess') : `+${skills.length - maxNumberOfSkillsDisplayed}`}
              </a>
            )}
          </div>
        </section>
      )}
    </Fragment>
  );
};
